import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        margin: '20px 0',
        '@media (max-width: 400px)': {
            gap: '4px',
        },
    },
    input: {
        width: '48px',
        height: '56px',
        textAlign: 'center',
        fontSize: '24px',
        fontWeight: 'bold',
        border: '2px solid #e0e0e0',
        borderRadius: '8px',
        outline: 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:focus': {
            borderColor: '#6f2981',
            boxShadow: '0 0 0 3px rgba(111, 41, 129, 0.1)',
        },
        '&:hover': {
            borderColor: '#b0b0b0',
        },
        '@media (max-width: 400px)': {
            width: '40px',
            height: '48px',
            fontSize: '20px',
        },
    },
    inputError: {
        borderColor: '#f44336',
        '&:focus': {
            borderColor: '#f44336',
            boxShadow: '0 0 0 3px rgba(244, 67, 54, 0.1)',
        },
    },
    inputFilled: {
        borderColor: '#6f2981',
        backgroundColor: '#faf5fc',
    },
}));

const OtpInput = ({ length = 6, value = '', onChange, onComplete, error = false, disabled = false }) => {
    const classes = useStyles();
    const [otp, setOtp] = useState(new Array(length).fill(''));
    const inputRefs = useRef([]);

    useEffect(() => {
        // Initialize refs
        inputRefs.current = inputRefs.current.slice(0, length);
    }, [length]);

    useEffect(() => {
        // Sync with external value
        if (value) {
            const otpArray = value.split('').slice(0, length);
            while (otpArray.length < length) {
                otpArray.push('');
            }
            setOtp(otpArray);
        } else {
            setOtp(new Array(length).fill(''));
        }
    }, [value, length]);

    useEffect(() => {
        // Focus first input on mount
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    const handleChange = (element, index) => {
        const val = element.value;

        // Only allow numeric input
        if (!/^\d*$/.test(val)) return;

        // Handle paste of full OTP
        if (val.length > 1) {
            const pastedValue = val.slice(0, length);
            const newOtp = [...otp];
            for (let i = 0; i < pastedValue.length && index + i < length; i++) {
                if (/^\d$/.test(pastedValue[i])) {
                    newOtp[index + i] = pastedValue[i];
                }
            }
            setOtp(newOtp);
            const otpString = newOtp.join('');
            onChange?.(otpString);

            // Focus last filled input or next empty
            const lastFilledIndex = Math.min(index + pastedValue.length - 1, length - 1);
            inputRefs.current[lastFilledIndex]?.focus();

            if (otpString.length === length && /^\d+$/.test(otpString)) {
                onComplete?.(otpString);
            }
            return;
        }

        // Handle single character input
        const newOtp = [...otp];
        newOtp[index] = val.slice(-1);
        setOtp(newOtp);

        const otpString = newOtp.join('');
        onChange?.(otpString);

        // Move to next input
        if (val && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        }

        // Check if complete
        if (otpString.length === length && /^\d+$/.test(otpString)) {
            onComplete?.(otpString);
        }
    };

    const handleKeyDown = (e, index) => {
        // Handle backspace
        if (e.key === 'Backspace') {
            if (!otp[index] && index > 0) {
                // Move to previous input and clear it
                const newOtp = [...otp];
                newOtp[index - 1] = '';
                setOtp(newOtp);
                onChange?.(newOtp.join(''));
                inputRefs.current[index - 1]?.focus();
            } else {
                // Clear current input
                const newOtp = [...otp];
                newOtp[index] = '';
                setOtp(newOtp);
                onChange?.(newOtp.join(''));
            }
            e.preventDefault();
        }

        // Handle arrow keys
        if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'ArrowRight' && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();

        // Only allow numeric paste
        if (!/^\d+$/.test(pastedData)) return;

        const pastedOtp = pastedData.slice(0, length).split('');
        const newOtp = [...otp];

        for (let i = 0; i < pastedOtp.length; i++) {
            newOtp[i] = pastedOtp[i];
        }

        setOtp(newOtp);
        const otpString = newOtp.join('');
        onChange?.(otpString);

        // Focus appropriate input
        const focusIndex = Math.min(pastedOtp.length, length - 1);
        inputRefs.current[focusIndex]?.focus();

        if (otpString.length === length) {
            onComplete?.(otpString);
        }
    };

    const handleFocus = (e) => {
        e.target.select();
    };

    return (
        <div className={classes.container}>
            {otp.map((digit, index) => (
                <input
                    key={index}
                    ref={(ref) => (inputRefs.current[index] = ref)}
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={length}
                    value={digit}
                    onChange={(e) => handleChange(e.target, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={handlePaste}
                    onFocus={handleFocus}
                    disabled={disabled}
                    className={`${classes.input} ${error ? classes.inputError : ''} ${digit ? classes.inputFilled : ''}`}
                    aria-label={`Digit ${index + 1} of ${length}`}
                />
            ))}
        </div>
    );
};

export default OtpInput;
